(() => {
  const CPO_PATTERN =
    /Certified\s+Pre[-\s]?Owned/i;

  const DONOR_PATTERN =
    /#\d+/;

  const XPART_PATTERN =
    /XPart\s+Premium/i;

  let productData = null;
  let isAdding = false;

  function cleanText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getLabel(input) {
    if (input.id) {
      const label =
        document.querySelector(
          `label[for="${CSS.escape(input.id)}"]`
        );

      if (label) {
        return label;
      }
    }

    return input.closest('label');
  }

  function getShopRoot() {
    return (
      window.Shopify &&
      Shopify.routes &&
      Shopify.routes.root
    )
      ? Shopify.routes.root
      : '/';
  }

  function getProductHandle() {
    const match =
      window.location.pathname.match(
        /\/products\/([^/?#]+)/i
      );

    return match
      ? decodeURIComponent(match[1])
      : null;
  }

  function money(cents) {
    const value =
      Number(cents);

    if (!Number.isFinite(value)) {
      return '';
    }

    return `$${(
      value / 100
    ).toFixed(2)}`;
  }

  /*
   * --------------------------------------------------
   * LOAD LIVE PRODUCT DATA
   * --------------------------------------------------
   */

  async function loadProductData() {
    const handle =
      getProductHandle();

    if (!handle) {
      throw new Error(
        'Product handle not found.'
      );
    }

    const root =
      getShopRoot();

    const url =
      `${root}products/${encodeURIComponent(handle)}.js?xpart=${Date.now()}`;

    const response =
      await fetch(
        url,
        {
          method: 'GET',

          credentials:
            'same-origin',

          cache:
            'no-store',

          headers: {
            Accept:
              'application/json'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        'Unable to check product inventory.'
      );
    }

    productData =
      await response.json();

    return productData;
  }

  /*
   * --------------------------------------------------
   * AVAILABLE XPART PREMIUM
   * --------------------------------------------------
   */

  function getAvailableXPartVariant() {
    if (
      !productData ||
      !Array.isArray(
        productData.variants
      )
    ) {
      return null;
    }

    return (
      productData.variants.find(
        variant => {
          const title =
            cleanText(
              variant.public_title ||
              variant.title
            );

          return (
            XPART_PATTERN.test(title) &&
            variant.available === true
          );
        }
      ) || null
    );
  }

  /*
   * --------------------------------------------------
   * AVAILABLE CPO VARIANTS
   * --------------------------------------------------
   */

  function getAvailableCpoVariants() {
    if (
      !productData ||
      !Array.isArray(
        productData.variants
      )
    ) {
      return [];
    }

    const availableCpo =
      productData.variants.filter(
        variant => {
          const title =
            cleanText(
              variant.public_title ||
              variant.title
            );

          return (
            CPO_PATTERN.test(title) &&
            variant.available === true
          );
        }
      );

    /*
     * Prefer donor-specific variants.
     */
    const donorSpecific =
      availableCpo.filter(
        variant => {
          const title =
            cleanText(
              variant.public_title ||
              variant.title
            );

          return DONOR_PATTERN.test(
            title
          );
        }
      );

    return donorSpecific.length
      ? donorSpecific
      : availableCpo;
  }

  /*
   * --------------------------------------------------
   * HIDE ORIGINAL CPO BUTTONS
   * --------------------------------------------------
   */

  function hideOriginalCpoChoices() {
    const radios =
      Array.from(
        document.querySelectorAll(
          'input[type="radio"]'
        )
      );

    radios.forEach(
      input => {
        const label =
          getLabel(input);

        if (!label) {
          return;
        }

        const labelText =
          cleanText(
            label.textContent
          );

        if (
          !CPO_PATTERN.test(
            labelText
          )
        ) {
          return;
        }

        label.style.display =
          'none';

        input.style.position =
          'absolute';

        input.style.opacity =
          '0';

        input.style.pointerEvents =
          'none';

        input.tabIndex =
          -1;
      }
    );
  }

  /*
   * --------------------------------------------------
   * FIND SAFE BUYING OPTIONS CONTAINER
   * --------------------------------------------------
   */

  function findSafeContainer() {
    const radios =
      Array.from(
        document.querySelectorAll(
          'input[type="radio"]'
        )
      );

    const items =
      radios
        .map(
          input => ({
            input,
            label:
              getLabel(input)
          })
        )
        .filter(
          item =>
            item.label
        )
        .map(
          item => ({
            ...item,

            text:
              cleanText(
                item.label.textContent
              )
          })
        );

    const xpart =
      items.find(
        item =>
          XPART_PATTERN.test(
            item.text
          )
      );

    const cpoLabels =
      items
        .filter(
          item =>
            CPO_PATTERN.test(
              item.text
            )
        )
        .map(
          item =>
            item.label
        );

    if (
      xpart &&
      cpoLabels.length
    ) {
      let parent =
        xpart.label.parentElement;

      while (
        parent &&
        parent !== document.body
      ) {
        const containsCpo =
          cpoLabels.some(
            label =>
              parent.contains(label)
          );

        if (containsCpo) {
          return parent;
        }

        parent =
          parent.parentElement;
      }
    }

    if (xpart) {
      return (
        xpart.label.closest(
          'fieldset'
        ) ||
        xpart.label.parentElement
      );
    }

    if (cpoLabels.length) {
      return (
        cpoLabels[0].closest(
          'fieldset'
        ) ||
        cpoLabels[0]
          .parentElement
      );
    }

    return null;
  }

  /*
   * --------------------------------------------------
   * DEFAULT = XPART PREMIUM
   * --------------------------------------------------
   */

  function selectXPartPremiumDefault() {
    const radios =
      Array.from(
        document.querySelectorAll(
          'input[type="radio"]'
        )
      );

    const options =
      radios
        .map(
          input => ({
            input,
            label:
              getLabel(input)
          })
        )
        .filter(
          item =>
            item.label
        )
        .map(
          item => ({
            ...item,

            text:
              cleanText(
                item.label.textContent
              )
          })
        );

    let preferred =
      options.find(
        item =>
          !item.input.disabled &&
          XPART_PATTERN.test(
            item.text
          )
      );

    if (!preferred) {
      preferred =
        options.find(
          item =>
            !item.input.disabled &&
            !CPO_PATTERN.test(
              item.text
            )
        );
    }

    if (!preferred) {
      return;
    }

    if (
      !preferred.input.checked
    ) {
      preferred.input.click();
    } else {
      preferred.input.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );
    }
  }

  /*
   * --------------------------------------------------
   * PRICE DISPLAY
   * --------------------------------------------------
   */

  function getDisplayPrice(
    variants
  ) {
    if (!variants.length) {
      return '';
    }

    const prices =
      variants
        .map(
          variant =>
            Number(
              variant.price
            )
        )
        .filter(
          Number.isFinite
        );

    if (!prices.length) {
      return '';
    }

    const unique =
      [...new Set(prices)];

    if (
      unique.length === 1
    ) {
      return money(
        unique[0]
      );
    }

    return `From ${money(
      Math.min(...prices)
    )}`;
  }

  /*
   * --------------------------------------------------
   * ADD EXACT VARIANT TO CART
   * --------------------------------------------------
   */

  async function addVariantToCart(
    variantId
  ) {
    const root =
      getShopRoot();

    const response =
      await fetch(
        `${root}cart/add.js`,
        {
          method:
            'POST',

          credentials:
            'same-origin',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json'
          },

          body:
            JSON.stringify({
              items: [
                {
                  id:
                    Number(
                      variantId
                    ),

                  quantity: 1
                }
              ]
            })
        }
      );

    const data =
      await response.json();

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Unable to add this item.'
      );
    }

    return data;
  }

  /*
   * --------------------------------------------------
   * XPART RED BOX QUICK ADD
   * --------------------------------------------------
   */

  function attachXPartQuickAdd() {
    const radios =
      Array.from(
        document.querySelectorAll(
          'input[type="radio"]'
        )
      );

    const xpart =
      radios
        .map(
          input => ({
            input,
            label:
              getLabel(input)
          })
        )
        .filter(
          item =>
            item.label
        )
        .find(
          item =>
            XPART_PATTERN.test(
              cleanText(
                item.label.textContent
              )
            )
        );

    if (
      !xpart ||
      xpart.label.dataset
        .xpartQuickAddBound ===
        'true'
    ) {
      return;
    }

    xpart.label.dataset
      .xpartQuickAddBound =
      'true';

    xpart.label.style.cursor =
      'pointer';

    xpart.label.addEventListener(
      'click',
      async event => {
        /*
         * Only a real customer click
         * quick-adds to cart.
         */
        if (
          !event.isTrusted ||
          isAdding
        ) {
          return;
        }

        isAdding =
          true;

        try {
          await loadProductData();

          const variant =
            getAvailableXPartVariant();

          if (!variant) {
            throw new Error(
              'XPart Premium is currently out of stock.'
            );
          }

          await addVariantToCart(
            variant.id
          );

          const root =
            getShopRoot();

          window.location.href =
            `${root}cart`;

        } catch (error) {
          console.error(
            'XPart Premium quick-add error:',
            error
          );

          isAdding =
            false;

          alert(
            error.message ||
            'Unable to add XPart Premium. Please try again.'
          );
        }
      }
    );
  }

  /*
   * --------------------------------------------------
   * GREEN CPO QUICK ADD
   * --------------------------------------------------
   */

  async function addFirstAvailableCpo(
    button,
    textEl,
    priceEl,
    countEl
  ) {
    if (isAdding) {
      return;
    }

    isAdding =
      true;

    button.disabled =
      true;

    const normalText =
      'Add a Certified Pre-Owned Part';

    textEl.textContent =
      'Adding Certified Pre-Owned Part…';

    try {
      /*
       * Check stock live at click time.
       */
      await loadProductData();

      const available =
        getAvailableCpoVariants();

      if (!available.length) {
        throw new Error(
          'No Certified Pre-Owned parts are currently in stock.'
        );
      }

      let lastError =
        null;

      /*
       * Try each available donor in order.
       */
      for (
        const variant of available
      ) {
        try {
          await addVariantToCart(
            variant.id
          );

          const root =
            getShopRoot();

          window.location.href =
            `${root}cart`;

          return;

        } catch (error) {
          lastError =
            error;
        }
      }

      throw (
        lastError ||
        new Error(
          'No Certified Pre-Owned parts are currently available.'
        )
      );

    } catch (error) {
      console.error(
        'XPart CPO add error:',
        error
      );

      try {
        await loadProductData();
      } catch (refreshError) {
        // Ignore.
      }

      const available =
        getAvailableCpoVariants();

      /*
       * If CPO stock has now hit zero,
       * remove the entire green CPO area.
       */
      if (!available.length) {
        const picker =
          document.querySelector(
            '.xpart-cpo-picker'
          );

        if (picker) {
          picker.remove();
        }

        isAdding =
          false;

        return;
      }

      countEl.textContent =
        `${available.length} available`;

      priceEl.textContent =
        getDisplayPrice(
          available
        );

      textEl.textContent =
        normalText;

      button.disabled =
        false;

      isAdding =
        false;

      alert(
        error.message ||
        'Unable to add a Certified Pre-Owned part. Please try again.'
      );
    }
  }

  /*
   * --------------------------------------------------
   * BUILD GREEN CPO BUTTON
   * --------------------------------------------------
   */

  function buildCpoButton() {
    if (
      document.querySelector(
        '.xpart-cpo-picker'
      )
    ) {
      return;
    }

    const available =
      getAvailableCpoVariants();

    /*
     * --------------------------------------------------
     * IMPORTANT NEW RULE
     *
     * ZERO CPO INVENTORY =
     * CREATE NOTHING.
     *
     * No heading.
     * No green box.
     * No sold-out row.
     * --------------------------------------------------
     */
    if (!available.length) {
      return;
    }

    const container =
      findSafeContainer();

    if (!container) {
      return;
    }

    const picker =
      document.createElement(
        'div'
      );

    picker.className =
      'xpart-cpo-picker';

    picker.dataset.xpartCpoCustom =
      'true';

    /*
     * Heading
     */
    const heading =
      document.createElement(
        'div'
      );

    heading.className =
      'xpart-cpo-picker__heading';

    const headingLabel =
      document.createElement(
        'span'
      );

    headingLabel.className =
      'xpart-cpo-picker__heading-label';

    headingLabel.textContent =
      'Certified Pre-Owned: ';

    const headingValue =
      document.createElement(
        'strong'
      );

    headingValue.className =
      'xpart-cpo-picker__heading-value';

    headingValue.textContent =
      `${available.length} available`;

    heading.appendChild(
      headingLabel
    );

    heading.appendChild(
      headingValue
    );

    /*
     * Green button
     */
    const button =
      document.createElement(
        'button'
      );

    button.type =
      'button';

    button.className =
      'xpart-cpo-trigger';

    const content =
      document.createElement(
        'span'
      );

    content.className =
      'xpart-cpo-trigger__content';

    const text =
      document.createElement(
        'span'
      );

    text.className =
      'xpart-cpo-trigger__text';

    text.textContent =
      'Add a Certified Pre-Owned Part';

    const price =
      document.createElement(
        'span'
      );

    price.className =
      'xpart-cpo-trigger__price';

    price.textContent =
      getDisplayPrice(
        available
      );

    content.appendChild(
      text
    );

    if (
      price.textContent
    ) {
      content.appendChild(
        price
      );
    }

    button.appendChild(
      content
    );

    button.addEventListener(
      'click',
      event => {
        event.preventDefault();
        event.stopPropagation();

        addFirstAvailableCpo(
          button,
          text,
          price,
          headingValue
        );
      },
      true
    );

    picker.appendChild(
      heading
    );

    picker.appendChild(
      button
    );

    container.appendChild(
      picker
    );
  }

  /*
   * --------------------------------------------------
   * REPAIR AFTER THIRD-PARTY APP REDRAWS
   * --------------------------------------------------
   */

  function repairDisplay() {
    hideOriginalCpoChoices();

    attachXPartQuickAdd();

    const available =
      getAvailableCpoVariants();

    const picker =
      document.querySelector(
        '.xpart-cpo-picker'
      );

    /*
     * If no CPO is available,
     * make absolutely sure our
     * custom CPO section is gone.
     */
    if (!available.length) {
      if (picker) {
        picker.remove();
      }

      return;
    }

    /*
     * If CPO inventory exists and
     * the app/theme removed our box,
     * rebuild it.
     */
    if (!picker) {
      buildCpoButton();
    }
  }

  /*
   * --------------------------------------------------
   * START
   * --------------------------------------------------
   */

  async function init() {
    try {
      await loadProductData();

      hideOriginalCpoChoices();

      /*
       * This now builds ONLY when
       * at least one CPO is available.
       */
      buildCpoButton();

      attachXPartQuickAdd();

      window.setTimeout(
        selectXPartPremiumDefault,
        900
      );

      const observer =
        new MutationObserver(
          () => {
            window.requestAnimationFrame(
              repairDisplay
            );
          }
        );

      observer.observe(
        document.body,
        {
          childList: true,
          subtree: true
        }
      );

    } catch (error) {
      console.error(
        'XPart buying options initialization error:',
        error
      );
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
})();